import { useState, useEffect, useCallback } from "react";
import { db } from "../../db";
import {
  Btn, Modal, Field, Inp, Sel, Txta, FormRow, Empty, Spinner,
  Stage, StageTabs, BlockHead, KpiStrip, ExpandTable, Chips, MiniBar, Check, Selo, Explica,
} from "../../components/primitives";
import { STATUS_CFG, AMBIENTE_CFG } from "./FluxoMedClientes";
import { INSTANCIAS_CONHECIDAS, SUPABASE_PROJECTS, ESPECIALIDADES_MEDICAS, CONVENIOS_COMUNS } from "./constants";
import { TabImplantacao } from "./TabImplantacao";
import { PendenciasWidget } from "./FluxoMedCentral";
import { getClienteHealth, canAdvanceTo, PIPELINE_STATUS_CFG } from "./fluxomedHealth";

function ComboInp({ listId, value, onChange, placeholder, options }) {
  return (
    <>
      <input
        list={listId}
        className="input"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        style={{ width: "100%" }}
      />
      <datalist id={listId}>
        {options.map((o) =>
          typeof o === "string"
            ? <option key={o} value={o} />
            : <option key={o.value} value={o.value}>{o.label}</option>
        )}
      </datalist>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────
const STATUSES  = ["rascunho", "homologacao", "implantacao", "pronto_producao", "producao", "pausado"];
const AMBIENTES = ["lab", "producao", "template"];
const TEMPLATES = ["fluxomed-v1", "fluxomed-v1.1", "fluxomed-v2", ""];

const DIAS_SEMANA = [
  { v: "segunda", l: "Segunda-feira" },
  { v: "terca",   l: "Terça-feira"   },
  { v: "quarta",  l: "Quarta-feira"  },
  { v: "quinta",  l: "Quinta-feira"  },
  { v: "sexta",   l: "Sexta-feira"   },
  { v: "sabado",  l: "Sábado"        },
];

const TIPOS_MENSAGEM = [
  { tipo: "menu_inicial",         label: "Menu Inicial",               desc: "Primeira mensagem enviada ao paciente" },
  { tipo: "saudacao_retorno",     label: "Saudação de Retorno",        desc: "Paciente já iniciou conversa antes" },
  { tipo: "coleta_nome",          label: "Coleta de Nome",             desc: "Pedindo o nome do paciente" },
  { tipo: "selecao_regiao",       label: "Seleção de Região",          desc: "Listando regiões disponíveis" },
  { tipo: "selecao_unidade",      label: "Seleção de Unidade",         desc: "Listando unidades da região escolhida" },
  { tipo: "confirmacao_unidade",  label: "Confirmação de Unidade",     desc: "Confirmando dados da unidade escolhida" },
  { tipo: "docs_opcao",           label: "Envio de Documentos",        desc: "Perguntando se o paciente quer enviar docs agora" },
  { tipo: "confirmacao_humana",   label: "Encaminhamento à Secretária", desc: "Notificando que a secretária irá contatar" },
  { tipo: "convenio_recusado",    label: "Convênio Não Atendido",      desc: "Convênio não é aceito pelo médico" },
  { tipo: "followup",             label: "Follow-up",                  desc: "Reengajamento após 4h sem resposta" },
  { tipo: "encerramento",         label: "Encerramento",               desc: "Finalização da conversa" },
];

const HOMOLOGACAO_ITEMS = [
  { id: "dados_medico",      label: "Dados do médico preenchidos" },
  { id: "secretaria",        label: "Secretária configurada com WhatsApp" },
  { id: "convenios",         label: "Convênios aceitos e recusados definidos" },
  { id: "unidades",          label: "Pelo menos uma unidade com horário cadastrado" },
  { id: "mensagens_core",    label: "Mensagens obrigatórias preenchidas (menu, convênio, follow-up)" },
  { id: "followup_config",   label: "Follow-up configurado" },
  { id: "sandbox_teste",     label: "Fluxo testado no LAB/Sandbox" },
  { id: "fluxo_completo",    label: "Fluxo completo validado (menu → convênio → unidade → confirmação)" },
  { id: "secretaria_alerta", label: "Secretária informada sobre o funcionamento do bot" },
  { id: "cliente_aprovado",  label: "Cliente aprovou o fluxo" },
];

// ─────────────────────────────────────────────────────────────
// Utilitários
// ─────────────────────────────────────────────────────────────
function slugify(str) {
  return str
    .normalize("NFD").replace(/[0300-036f]/g, "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function SavedLabel({ show }) {
  if (!show) return null;
  return (
    <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 600 }}>✓ Salvo</span>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Geral
// ─────────────────────────────────────────────────────────────
function TabGeral({ cliente, implantacao, onUpdate }) {
  const [f, setF] = useState({ ...cliente });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const health        = getClienteHealth(f, implantacao);
  const gateBlock     = f.status === "producao" && !health.podeLancamento;
  const gateCheck     = f.status !== "producao" ? canAdvanceTo(cliente, implantacao, f.status) : { allowed: true };
  const saveBlocked   = gateBlock || (f.status !== cliente.status && !gateCheck.allowed);

  async function save() {
    setSaving(true);
    try {
      await onUpdate(f);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tab-pane">
      <BlockHead title="Dados do cliente" sub="Identificação, médico e secretária. Salve no fim da página.">
        <SavedLabel show={saved} />
        <Btn sm variant="primary" onClick={save} disabled={saving || saveBlocked}>
          {saving ? "Salvando..." : "Salvar"}
        </Btn>
      </BlockHead>

      <div style={{ display: "grid", gap: 12 }}>
        {/* Saúde do cliente */}
        {health.totalPendencias > 0 && (
          <PendenciasWidget cliente={f} implantacao={implantacao} />
        )}

        <Section title="Identificação">
          <FormRow>
            <Field label="Nome do cliente" half>
              <Inp value={f.nome} onChange={(e) => up("nome", e.target.value)} />
            </Field>
            <Field label="Slug" half>
              <Inp value={f.slug || ""} onChange={(e) => up("slug", slugify(e.target.value))}
                placeholder="Ex: dr-victor-couto" />
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Status" half>
              <Sel value={f.status} onChange={(e) => up("status", e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
              </Sel>
            </Field>
            <Field label="Ambiente" half>
              <Sel value={f.ambiente} onChange={(e) => up("ambiente", e.target.value)}>
                {AMBIENTES.map((a) => <option key={a} value={a}>{AMBIENTE_CFG[a].label}</option>)}
              </Sel>
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Template de origem" half>
              <Sel value={f.origem_template || ""} onChange={(e) => up("origem_template", e.target.value)}>
                {TEMPLATES.map((t) => <option key={t} value={t}>{t || "Personalizado"}</option>)}
              </Sel>
            </Field>
            <Field label="Instância Evolution API" half>
              <ComboInp
                listId="instancias-geral"
                value={f.instancia_evolution || ""}
                onChange={(e) => up("instancia_evolution", e.target.value)}
                placeholder="Ex: dr-victor"
                options={INSTANCIAS_CONHECIDAS}
              />
            </Field>
          </FormRow>
          <Field label="Projeto Supabase (operacional do bot)">
            <ComboInp
              listId="supabase-projects"
              value={f.supabase_project || ""}
              onChange={(e) => up("supabase_project", e.target.value)}
              placeholder="Ex: qbkhttznrbdzaeyuqtjm"
              options={SUPABASE_PROJECTS}
            />
          </Field>
        </Section>

        <Section title="Médico" aside={f.medico_nome || "não preenchido"}>
          <FormRow>
            <Field label="Nome" half>
              <Inp value={f.medico_nome || ""} onChange={(e) => up("medico_nome", e.target.value)}
                placeholder="Ex: Dr. Victor Couto" />
            </Field>
            <Field label="Especialidade" half>
              <ComboInp
                listId="especialidades-geral"
                value={f.medico_especialidade || ""}
                onChange={(e) => up("medico_especialidade", e.target.value)}
                placeholder="Ex: Dermatologia"
                options={ESPECIALIDADES_MEDICAS}
              />
            </Field>
          </FormRow>
          <Field label="CRM">
            <Inp value={f.medico_crm || ""} onChange={(e) => up("medico_crm", e.target.value)}
              placeholder="Ex: 12345-BA" style={{ maxWidth: 200 }} />
          </Field>
        </Section>

        <Section title="Secretária" aside={f.secretaria_nome || "não preenchida"}>
          <FormRow>
            <Field label="Nome" half>
              <Inp value={f.secretaria_nome || ""} onChange={(e) => up("secretaria_nome", e.target.value)}
                placeholder="Ex: Carol" />
            </Field>
            <Field label="WhatsApp (com DDI)" half>
              <Inp value={f.secretaria_whatsapp || ""} onChange={(e) => up("secretaria_whatsapp", e.target.value)}
                placeholder="Ex: 557191234567" />
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Horário início" half>
              <Inp type="time" value={f.secretaria_horario_inicio || "08:00"}
                onChange={(e) => up("secretaria_horario_inicio", e.target.value)} />
            </Field>
            <Field label="Horário fim" half>
              <Inp type="time" value={f.secretaria_horario_fim || "18:00"}
                onChange={(e) => up("secretaria_horario_fim", e.target.value)} />
            </Field>
          </FormRow>
        </Section>

        <Section title="Notas gerais">
          <Txta rows={3} value={f.notas || ""}
            onChange={(e) => up("notas", e.target.value)}
            placeholder="Observações sobre a implantação, contatos, histórico..." />
        </Section>

        {/* Gate bloqueado */}
        {saveBlocked && (
          <div className="alert-line bad" style={{ marginBottom: 0 }}>
            <span className="sev bad" />
            <span>
              <strong>Gate de produção:</strong>{" "}
              {gateCheck?.motivo || "Existem pendências obrigatórias antes de entrar em produção."}
              {" "}Resolva as pendências acima para avançar.
            </span>
          </div>
        )}

        <div className="detail-actions">
          <Btn variant="primary" onClick={save} disabled={saving || saveBlocked}>
            {saving ? "Salvando..." : "Salvar alterações"}
          </Btn>
          <SavedLabel show={saved} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Convênios
// ─────────────────────────────────────────────────────────────
function TabConvenios({ clienteId }) {
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [novoNome, setNovoNome]   = useState("");
  const [adding, setAdding]       = useState(false);
  const [filtro, setFiltro]       = useState("todos");

  const load = useCallback(async () => {
    const data = await db("fluxomed_convenios", "GET", null, `?cliente_id=eq.${clienteId}&order=created_at`);
    setConvenios(data || []);
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!novoNome.trim()) return;
    setAdding(true);
    // Suporta múltiplos separados por vírgula: "Unimed, Bradesco" → 2 registros
    const nomes = novoNome.split(",").map((n) => n.trim()).filter(Boolean);
    for (const nome of nomes) {
      await db("fluxomed_convenios", "POST", { cliente_id: clienteId, nome, aceito: true });
    }
    setNovoNome("");
    await load();
    setAdding(false);
  }

  async function toggle(conv) {
    await db("fluxomed_convenios", "PATCH", { aceito: !conv.aceito }, `?id=eq.${conv.id}`);
    setConvenios((prev) => prev.map((c) => c.id === conv.id ? { ...c, aceito: !conv.aceito } : c));
  }

  async function remove(id) {
    await db("fluxomed_convenios", "DELETE", null, `?id=eq.${id}`);
    setConvenios((prev) => prev.filter((c) => c.id !== id));
  }

  const aceitos   = convenios.filter((c) => c.aceito);
  const recusados = convenios.filter((c) => !c.aceito);
  const lista     = filtro === "aceitos" ? aceitos : filtro === "recusados" ? recusados : [...aceitos, ...recusados];

  if (loading) return <LoadingInline />;

  return (
    <div className="tab-pane">
      <BlockHead title="Convênios" sub="Aceito segue o fluxo; não atendido recebe a mensagem de recusa." />

      <KpiStrip style={{ marginBottom: 14 }} items={[
        { label: "Aceitos", value: aceitos.length },
        { label: "Não atendidos", value: recusados.length, tone: recusados.length ? "warn" : "", sub: recusados.length ? "bot encerra a conversa" : "nenhum" },
        { label: "Total", value: convenios.length },
      ]} />

      {/* Adicionar — suporta vírgula para múltiplos */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <input
            list="convenios-list"
            className="input"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Unimed (ou vários: Unimed, Bradesco, SulAmérica)"
            autoComplete="off"
            style={{ width: "100%" }}
          />
          <datalist id="convenios-list">
            {CONVENIOS_COMUNS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <Btn variant="primary" onClick={add} disabled={adding || !novoNome.trim()}>
          + Adicionar
        </Btn>
      </div>

      {convenios.length === 0 ? (
        <Empty icon="◎" text="Nenhum convênio cadastrado. Adicione os convênios aceitos e recusados." />
      ) : (
        <>
          <div className="tab-bar">
            <Chips label="Filtrar convênios" active={filtro} onChange={setFiltro} items={[
              { id: "todos", label: "Todos", count: convenios.length },
              { id: "aceitos", label: "Aceitos", count: aceitos.length },
              { id: "recusados", label: "Não atendidos", count: recusados.length },
            ]} />
          </div>
          <div className="klist">
            {lista.length === 0 && (
              <div className="klist-row"><span className="txt" style={{ color: "var(--faint)" }}>Nada neste filtro.</span></div>
            )}
            {lista.map((c) => (
              <div key={c.id} className="klist-row">
                <span className="txt" style={{ fontWeight: 600 }}>{c.nome}</span>
                <Selo tone={c.aceito ? "ok" : "bad"}>{c.aceito ? "Aceito" : "Não atendido"}</Selo>
                <Btn sm variant="ghost" onClick={() => toggle(c)}>{c.aceito ? "Recusar" : "Aceitar"}</Btn>
                <button type="button" aria-label={`Remover ${c.nome}`} onClick={() => remove(c.id)}
                  style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 15, padding: "0 4px" }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Unidades
// ─────────────────────────────────────────────────────────────
function TabUnidades({ clienteId }) {
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [showNew, setShowNew]   = useState(false);

  const load = useCallback(async () => {
    const data = await db("fluxomed_unidades", "GET", null, `?cliente_id=eq.${clienteId}&order=created_at`);
    setUnidades(data || []);
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  async function save(data) {
    if (data.id) {
      await db("fluxomed_unidades", "PATCH", data, `?id=eq.${data.id}`);
    } else {
      await db("fluxomed_unidades", "POST", { ...data, cliente_id: clienteId });
    }
    await load();
    setEditModal(null);
    setShowNew(false);
  }

  async function remove(id) {
    if (!window.confirm("Remover esta unidade?")) return;
    await db("fluxomed_unidades", "DELETE", null, `?id=eq.${id}`);
    setUnidades((prev) => prev.filter((u) => u.id !== id));
  }

  async function toggleAtivo(u) {
    await db("fluxomed_unidades", "PATCH", { ativo: !u.ativo }, `?id=eq.${u.id}`);
    setUnidades((prev) => prev.map((x) => x.id === u.id ? { ...x, ativo: !u.ativo } : x));
  }

  if (loading) return <LoadingInline />;

  const diaLabel = (u) => DIAS_SEMANA.find((d) => d.v === u.dia_semana)?.l || u.dia_semana;

  return (
    <div className="tab-pane">
      <BlockHead title={`Unidades (${unidades.length})`} sub="Uma linha por clínica. Clique para ver endereço e ações.">
        <Btn sm variant="primary" onClick={() => setShowNew(true)}>+ Nova unidade</Btn>
      </BlockHead>

      <ExpandTable
        minWidth={560}
        rows={unidades}
        empty="Nenhuma unidade cadastrada. Adicione as clínicas e seus horários."
        columns={[
          { key: "nome", label: "Unidade", render: (u) => (
            <><div className="name">{u.nome}</div><div className="sub">{u.key}</div></>
          ) },
          { key: "regiao", label: "Região", render: (u) => u.regiao || <span className="sub">—</span> },
          { key: "horario", label: "Atende", render: (u) => (u.dia_semana && u.horario)
            ? <span>{diaLabel(u)} · {u.horario}{u.quinzenal ? <span className="sub"> · quinzenal</span> : null}</span>
            : <span className="sub">sem horário</span> },
          { key: "ativo", label: "Situação", render: (u) => <Selo tone={u.ativo ? "ok" : "off"}>{u.ativo ? "Ativa" : "Inativa"}</Selo> },
        ]}
        detail={(u) => (
          <div className="detail-grid">
            <dl className="facts">
              <dt>Endereço</dt><dd>{u.endereco || "—"}</dd>
              <dt>Chave</dt><dd style={{ fontFamily: "var(--font-data)" }}>{u.key}</dd>
            </dl>
            <div className="detail-actions">
              <Btn sm variant="secondary" onClick={() => setEditModal(u)}>Editar</Btn>
              <Btn sm variant="ghost" onClick={() => toggleAtivo(u)}>{u.ativo ? "Desativar" : "Ativar"}</Btn>
              <Btn sm variant="ghost" onClick={() => remove(u.id)}>Remover</Btn>
            </div>
          </div>
        )}
      />

      {(showNew || editModal) && (
        <UnidadeModal
          initial={editModal}
          onClose={() => { setShowNew(false); setEditModal(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function UnidadeModal({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial || {
    key: "", nome: "", regiao: "", endereco: "",
    dia_semana: "terca", horario: "", quinzenal: false, ativo: true,
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!f.nome.trim() || !f.key.trim()) return;
    setSaving(true);
    await onSave({ ...f, key: slugify(f.key) });
    setSaving(false);
  }

  return (
    <Modal title={initial ? "Editar unidade" : "Nova unidade"} onClose={onClose} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <FormRow>
          <Field label="Nome da unidade" half>
            <Inp value={f.nome} onChange={(e) => up("nome", e.target.value)}
              placeholder="Ex: Clínica IAD" autoFocus />
          </Field>
          <Field label="Chave (key)" half>
            <Inp value={f.key} onChange={(e) => up("key", e.target.value)}
              placeholder="Ex: iad" />
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Região / Bairro" half>
            <Inp value={f.regiao || ""} onChange={(e) => up("regiao", e.target.value)}
              placeholder="Ex: Pituba" />
          </Field>
          <Field label="Dia da semana" half>
            <Sel value={f.dia_semana} onChange={(e) => up("dia_semana", e.target.value)}>
              {DIAS_SEMANA.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Horário (HH:MM)" half>
            <Inp type="time" value={f.horario || ""} onChange={(e) => up("horario", e.target.value)} />
          </Field>
          <Field label=" " half>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 4 }}>
              <input
                type="checkbox" id="quinzenal"
                checked={!!f.quinzenal}
                onChange={(e) => up("quinzenal", e.target.checked)}
                style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--primary)" }}
              />
              <label htmlFor="quinzenal" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
                Quinzenal
              </label>
            </div>
          </Field>
        </FormRow>
        <Field label="Endereço completo">
          <Txta rows={2} value={f.endereco || ""} onChange={(e) => up("endereco", e.target.value)}
            placeholder="Ex: Av. Prof. Magalhães Neto, 1541 / Sala 1001 - Pituba" />
        </Field>
        <Btn variant="primary" full onClick={save} disabled={saving || !f.nome.trim() || !f.key.trim()}>
          {saving ? "Salvando..." : "Salvar unidade"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Mensagens
// ─────────────────────────────────────────────────────────────
function TabMensagens({ clienteId }) {
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [local, setLocal]         = useState({});
  const [saved, setSaved]         = useState({});
  const [saving, setSaving]       = useState({});

  const load = useCallback(async () => {
    const data = await db("fluxomed_mensagens", "GET", null, `?cliente_id=eq.${clienteId}`);
    setMensagens(data || []);
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  function getConteudo(tipo) {
    if (local[tipo] !== undefined) return local[tipo];
    const m = mensagens.find((x) => x.tipo === tipo);
    return m ? m.conteudo : "";
  }

  async function saveMensagem(tipo) {
    const conteudo = local[tipo] !== undefined ? local[tipo] : getConteudo(tipo);
    setSaving((p) => ({ ...p, [tipo]: true }));
    const existing = mensagens.find((m) => m.tipo === tipo);
    if (existing) {
      await db("fluxomed_mensagens", "PATCH", { conteudo, updated_at: new Date().toISOString() }, `?id=eq.${existing.id}`);
      setMensagens((prev) => prev.map((m) => m.id === existing.id ? { ...m, conteudo } : m));
    } else {
      const [created] = await db("fluxomed_mensagens", "POST", { cliente_id: clienteId, tipo, conteudo });
      setMensagens((prev) => [...prev, created]);
    }
    setLocal((p) => { const n = { ...p }; delete n[tipo]; return n; });
    setSaving((p) => ({ ...p, [tipo]: false }));
    setSaved((p) => ({ ...p, [tipo]: true }));
    setTimeout(() => setSaved((p) => ({ ...p, [tipo]: false })), 2500);
  }

  if (loading) return <LoadingInline />;

  const isDirty = (tipo) => local[tipo] !== undefined && local[tipo] !== (mensagens.find((m) => m.tipo === tipo)?.conteudo || "");
  const preenchidas = TIPOS_MENSAGEM.filter(({ tipo }) => (mensagens.find((m) => m.tipo === tipo)?.conteudo || "").trim()).length;

  return (
    <div className="tab-pane">
      <BlockHead title="Mensagens do bot" sub="Uma linha por mensagem. Clique para editar o texto.">
        <MiniBar done={preenchidas} total={TIPOS_MENSAGEM.length} />
      </BlockHead>

      <Explica title="Variáveis que o bot troca no texto">
        Use <span style={{ fontFamily: "var(--font-data)", color: "var(--primary)" }}>{"{nome}"} {"{unidade}"} {"{endereco}"} {"{dia}"} {"{hora}"}</span> dentro da mensagem.
      </Explica>
      <div style={{ height: 12 }} />

      <ExpandTable
        minWidth={520}
        rowKey={(r) => r.tipo}
        rows={TIPOS_MENSAGEM}
        columns={[
          { key: "label", label: "Mensagem", render: ({ label, desc }) => (
            <><div className="name">{label}</div><div className="sub">{desc}</div></>
          ) },
          { key: "sit", label: "Situação", render: ({ tipo }) => {
            if (saved[tipo]) return <Selo tone="ok">Salvo</Selo>;
            if (isDirty(tipo)) return <Selo tone="warn">Não salvo</Selo>;
            return getConteudo(tipo).trim() ? <Selo tone="ok" dot={false}>Preenchida</Selo> : <Selo tone="off">Vazia</Selo>;
          } },
        ]}
        detail={({ tipo, label }) => (
          <div className="detail-grid">
            <Txta
              rows={5}
              value={getConteudo(tipo)}
              onChange={(e) => setLocal((p) => ({ ...p, [tipo]: e.target.value }))}
              placeholder={`Texto da mensagem — ${label.toLowerCase()}...`}
              style={{ lineHeight: 1.65 }}
            />
            <div className="detail-actions">
              <Btn sm variant={isDirty(tipo) ? "primary" : "secondary"} onClick={() => saveMensagem(tipo)}
                disabled={saving[tipo]}>
                {saving[tipo] ? "Salvando..." : "Salvar"}
              </Btn>
              <SavedLabel show={saved[tipo]} />
            </div>
          </div>
        )}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Follow-up
// ─────────────────────────────────────────────────────────────
function TabFollowup({ cliente, onUpdate }) {
  const [f, setF] = useState({
    followup_ativo:                  cliente.followup_ativo !== false,
    followup_delay_horas:            cliente.followup_delay_horas ?? 4,
    followup_janela_inicio:          cliente.followup_janela_inicio || "08:00",
    followup_janela_fim:             cliente.followup_janela_fim || "20:00",
    followup_timeout_terminal_horas: cliente.followup_timeout_terminal_horas ?? 4,
    followup_timeout_followup_horas: cliente.followup_timeout_followup_horas ?? 24,
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  async function save() {
    setSaving(true);
    await onUpdate(f);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div className="tab-pane">
      <BlockHead title="Follow-up" sub="Reengaja o paciente que parou de responder.">
        <SavedLabel show={saved} />
        <Btn sm variant="primary" onClick={save} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Btn>
      </BlockHead>

      <KpiStrip style={{ marginBottom: 14 }} items={[
        { label: "Follow-up", value: f.followup_ativo ? "Ligado" : "Desligado", tone: f.followup_ativo ? "ok" : "warn", sub: f.followup_ativo ? "envia quando o paciente some" : "nenhum reengajamento" },
        { label: "Dispara após", value: `${f.followup_delay_horas} h`, sub: "sem resposta" },
        { label: "Janela", value: `${f.followup_janela_inicio}–${f.followup_janela_fim}`, sub: "horário de Brasília" },
      ]} />

      <div style={{ display: "grid", gap: 12 }}>
        <div className="klist">
          <div className="klist-row">
            <Check on={!!f.followup_ativo} onClick={() => up("followup_ativo", !f.followup_ativo)} label="Ativar follow-up" />
            <span className="txt">Enviar follow-up quando o paciente não responder</span>
            <Selo tone={f.followup_ativo ? "ok" : "off"}>{f.followup_ativo ? "Ligado" : "Desligado"}</Selo>
          </div>
        </div>

        <Section title="Disparo do follow-up">
          <FormRow>
            <Field label="Horas sem resposta antes de enviar" half>
              <Inp type="number" min="1" max="48" value={f.followup_delay_horas}
                onChange={(e) => up("followup_delay_horas", Number(e.target.value))} />
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Janela de início" half>
              <Inp type="time" value={f.followup_janela_inicio}
                onChange={(e) => up("followup_janela_inicio", e.target.value)} />
            </Field>
            <Field label="Janela de fim" half>
              <Inp type="time" value={f.followup_janela_fim}
                onChange={(e) => up("followup_janela_fim", e.target.value)} />
            </Field>
          </FormRow>
        </Section>

        <Section title="Timeouts de reset de sessão">
          <FormRow>
            <Field label="Reset de estados terminais (horas)" half>
              <Inp type="number" min="1" max="72" value={f.followup_timeout_terminal_horas}
                onChange={(e) => up("followup_timeout_terminal_horas", Number(e.target.value))} />
            </Field>
            <Field label="Reset aguardando follow-up (horas)" half>
              <Inp type="number" min="1" max="96" value={f.followup_timeout_followup_horas}
                onChange={(e) => up("followup_timeout_followup_horas", Number(e.target.value))} />
            </Field>
          </FormRow>
          <Explica title="O que cada reset faz">
            <strong>Terminal:</strong> estados <code>aguardando_confirmacao_humana</code> e <code>encerrado</code> são resetados após esse tempo.<br />
            <strong>Follow-up:</strong> estado <code>aguardando_retorno_followup</code> é resetado após esse tempo desde o envio.
          </Explica>
        </Section>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Homologação
// ─────────────────────────────────────────────────────────────
function TabHomologacao({ cliente, onUpdate }) {
  const checklist = (() => {
    const saved = Array.isArray(cliente.homologacao_checklist) ? cliente.homologacao_checklist : [];
    return HOMOLOGACAO_ITEMS.map((item) => ({
      ...item,
      ok: saved.find((s) => s.id === item.id)?.ok || false,
    }));
  })();

  const [items, setItems]     = useState(checklist);
  const [status, setStatus]   = useState(cliente.homologacao_status || "pendente");
  const [notas, setNotas]     = useState(cliente.homologacao_notas || "");
  const [resp, setResp]       = useState(cliente.homologacao_responsavel || "");
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  function toggleItem(id) {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, ok: !i.ok } : i));
  }

  const done  = items.filter((i) => i.ok).length;
  const total = items.length;
  const pct   = Math.round((done / total) * 100);

  async function save() {
    setSaving(true);
    await onUpdate({
      homologacao_status:      status,
      homologacao_checklist:   items.map(({ id, ok }) => ({ id, ok })),
      homologacao_notas:       notas,
      homologacao_responsavel: resp,
      homologacao_data:        status === "aprovado" ? new Date().toISOString() : cliente.homologacao_data,
    });
    if (status === "aprovado") {
      db("fluxomed_historico", "POST", {
        cliente_id: cliente.id,
        evento: "homologacao_concluida",
        descricao: `Homologação aprovada${resp ? ` por ${resp}` : ""}`,
      }).catch(() => {});
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const HOM_STATUS = {
    pendente:  { label: "Pendente",  tone: "off" },
    aprovado:  { label: "Aprovado",  tone: "ok"  },
    reprovado: { label: "Reprovado", tone: "bad" },
  };
  const hs = HOM_STATUS[status] || HOM_STATUS.pendente;

  return (
    <div className="tab-pane">
      <BlockHead title="Homologação" sub="Valide cada item com o cliente e no LAB antes da produção.">
        <SavedLabel show={saved} />
        <Btn sm variant="primary" onClick={save} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Btn>
      </BlockHead>

      <KpiStrip style={{ marginBottom: 12 }} items={[
        { label: "Concluído", value: `${pct}%`, tone: pct === 100 ? "ok" : pct >= 50 ? "" : "warn", sub: pct === 100 ? "checklist completo" : `${total - done} item(ns) faltando` },
        { label: "Itens", value: `${done}/${total}` },
        { label: "Resultado", value: hs.label, color: hs.tone === "ok" ? "var(--success)" : hs.tone === "bad" ? "var(--danger)" : undefined, sub: resp ? `por ${resp}` : "sem responsável" },
      ]} />

      <Explica title="O que é a homologação?">
        Antes de ativar o bot em produção, valide cada item abaixo com o cliente e no LAB.
        Só altere o status do cliente para <strong>Produção</strong> após marcar todos os
        itens críticos e registrar o resultado final.
      </Explica>

      <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
        <div className="klist">
          <div className="klist-head">
            <span className="eyebrow">Itens de validação</span>
            <MiniBar done={done} total={total} width={70} />
          </div>
          {items.map((item) => (
            <div key={item.id} className={`klist-row${item.ok ? " done" : ""}`} style={{ cursor: "pointer" }} onClick={() => toggleItem(item.id)}>
              <Check on={item.ok} onClick={() => toggleItem(item.id)} label={item.label} />
              <span className="txt">{item.label}</span>
            </div>
          ))}
        </div>

        <Section title="Resultado" aside={cliente.homologacao_data
          ? `aprovado em ${new Date(cliente.homologacao_data).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`
          : null}>
          <FormRow>
            <Field label="Status da homologação" half>
              <Sel value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.entries(HOM_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Sel>
            </Field>
            <Field label="Responsável" half>
              <Inp value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Ex: Rafael" />
            </Field>
          </FormRow>
          <Field label="Observações">
            <Txta rows={3} value={notas} onChange={(e) => setNotas(e.target.value)}
              placeholder="Problemas encontrados, pendências, notas do teste..." />
          </Field>
        </Section>

        <div className="detail-actions">
          <Btn variant="primary" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar homologação"}
          </Btn>
          <SavedLabel show={saved} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Exportar Configuração
// ─────────────────────────────────────────────────────────────
function TabExportar({ cliente, clienteId }) {
  const [loading, setLoading] = useState(false);
  const [json, setJson]       = useState(null);
  const [copiado, setCopiado] = useState(false);

  async function gerar() {
    setLoading(true);
    try {
      const [convenios, unidades, mensagens] = await Promise.all([
        db("fluxomed_convenios", "GET", null, `?cliente_id=eq.${clienteId}&order=created_at`),
        db("fluxomed_unidades",  "GET", null, `?cliente_id=eq.${clienteId}&ativo=eq.true&order=created_at`),
        db("fluxomed_mensagens", "GET", null, `?cliente_id=eq.${clienteId}`),
      ]);

      const config = {
        meta: {
          cliente:          cliente.slug,
          versao:           "1.0",
          exportado_em:     new Date().toISOString(),
          origem_template:  cliente.origem_template || "fluxomed-v1",
        },
        instancia:         cliente.instancia_evolution || "",
        supabase_project:  cliente.supabase_project || "",
        medico: {
          nome:           cliente.medico_nome || "",
          especialidade:  cliente.medico_especialidade || "",
          crm:            cliente.medico_crm || "",
        },
        secretaria: {
          nome:            cliente.secretaria_nome || "",
          whatsapp:        cliente.secretaria_whatsapp || "",
          horario_inicio:  cliente.secretaria_horario_inicio || "08:00",
          horario_fim:     cliente.secretaria_horario_fim || "18:00",
        },
        convenios: {
          aceitos: [...new Set(
            (convenios || []).filter((c) => c.aceito)
              .flatMap((c) => c.nome.split(",").map((n) => n.trim().toLowerCase()))
              .filter(Boolean)
          )],
          recusados: [...new Set(
            (convenios || []).filter((c) => !c.aceito)
              .flatMap((c) => c.nome.split(",").map((n) => n.trim().toLowerCase()))
              .filter(Boolean)
          )],
        },
        unidades: (unidades || []).reduce((acc, u) => {
          acc[u.key] = {
            nome:    u.nome,
            regiao:  u.regiao  || "",
            endereco: u.endereco || "",
            horarios: (u.dia_semana && u.horario)
              ? [{ dia: u.dia_semana, hora: u.horario, quinzenal: !!u.quinzenal }]
              : [],
          };
          return acc;
        }, {}),
        mensagens: (mensagens || []).reduce((acc, m) => {
          acc[m.tipo] = m.conteudo;
          return acc;
        }, {}),
        followup: {
          ativo:                      cliente.followup_ativo !== false,
          delay_horas:                cliente.followup_delay_horas ?? 4,
          janela_inicio:              cliente.followup_janela_inicio || "08:00",
          janela_fim:                 cliente.followup_janela_fim || "20:00",
          timeout_terminal_horas:     cliente.followup_timeout_terminal_horas ?? 4,
          timeout_followup_horas:     cliente.followup_timeout_followup_horas ?? 24,
        },
      };

      setJson(config);
      // Registrar evento de exportação (fire-and-forget)
      db("fluxomed_historico", "POST", {
        cliente_id: clienteId,
        evento: "exportacao_gerada",
        descricao: `Configuração exportada: config-${cliente.slug}.json`,
      }).catch(() => {});
    } finally {
      setLoading(false);
    }
  }

  function baixar() {
    if (!json) return;
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `config-${cliente.slug}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function copiar() {
    if (!json) return;
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <div className="tab-pane">
      <BlockHead title="Exportar configuração" sub={<>Gera o arquivo <strong>config-{cliente.slug}.json</strong> para importar à mão no workflow n8n.</>}>
        <Btn sm variant="primary" onClick={gerar} disabled={loading}>
          {loading ? "Gerando..." : "Gerar configuração"}
        </Btn>
      </BlockHead>

      <Explica title="O que entra no arquivo">
        Médico, secretária, convênios, unidades ativas, mensagens e follow-up.
        Nenhuma integração automática ocorre nesta fase: o arquivo é importado manualmente no n8n.
      </Explica>

      {json && (
        <div className="klist" style={{ marginTop: 12 }}>
          <div className="klist-head">
            <span className="eyebrow">config-{cliente.slug}.json</span>
            <div className="detail-actions">
              <Btn sm variant="secondary" onClick={baixar}>Baixar JSON</Btn>
              <Btn sm variant="ghost" onClick={copiar}>{copiado ? "✓ Copiado" : "Copiar"}</Btn>
            </div>
          </div>
          <pre style={{
            margin: 0, background: "var(--surface)", padding: "14px 18px", fontSize: 11.5,
            lineHeight: 1.7, color: "var(--ink)", overflow: "auto",
            maxHeight: 460, fontFamily: "var(--font-data)",
          }}>
            {JSON.stringify(json, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Aba Histórico
// ─────────────────────────────────────────────────────────────
const EVENTO_CFG = {
  cliente_criado:             { label: "Cliente criado",         tone: "info" },
  cliente_atualizado:         { label: "Cliente atualizado",     tone: "off"  },
  exportacao_gerada:          { label: "Exportação gerada",      tone: "info" },
  homologacao_concluida:      { label: "Homologação concluída",  tone: "ok"   },
  implantacao_iniciada:       { label: "Implantação iniciada",   tone: "info" },
  implantacao_item_concluido: { label: "Item concluído",         tone: "off"  },
  implantacao_concluida:      { label: "Implantação concluída",  tone: "ok"   },
  artefato_gerado:            { label: "Artefato gerado",        tone: "off"  },
  pipeline_avancado:          { label: "Pipeline avançado",      tone: "info" },
  cliente_producao:           { label: "Entrou em produção",     tone: "gold" },
  cliente_pausado:            { label: "Cliente pausado",        tone: "bad"  },
};

function TabHistorico({ clienteId }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await db("fluxomed_historico", "GET", null,
        `?cliente_id=eq.${clienteId}&order=created_at.desc`);
      setEventos(data || []);
    } catch (e) {
      console.error("Histórico: erro ao carregar:", e);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  function fmtEvento(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
      + " · " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  if (loading) return <LoadingInline />;

  return (
    <div className="tab-pane">
      <BlockHead title={`Histórico (${eventos.length})`} sub="Um evento por linha, do mais novo para o mais antigo." />
      {eventos.length === 0 ? (
        <Empty icon="◷" text="Nenhum evento registrado ainda." />
      ) : (
        <div className="klist">
          {eventos.map((ev) => {
            const cfg = EVENTO_CFG[ev.evento] || { label: ev.evento, tone: "off" };
            return (
              <div key={ev.id} className="klist-row">
                <Selo tone={cfg.tone}>{cfg.label}</Selo>
                <span className="txt" style={{ color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ev.descricao || ""}>
                  {ev.descricao || ""}
                </span>
                <span className="aside">{fmtEvento(ev.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers de layout
// ─────────────────────────────────────────────────────────────
function Section({ title, aside, children }) {
  return (
    <div className="klist">
      <div className="klist-head">
        <span className="eyebrow">{title}</span>
        {aside && <span className="aside" style={{ font: "500 11.5px var(--font-data)", color: "var(--faint)" }}>{aside}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "14px 18px 16px" }}>
        {children}
      </div>
    </div>
  );
}

function LoadingInline() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 0", color: "var(--muted)" }}>
      <Spinner size={18} />
      <span style={{ fontSize: 13 }}>Carregando...</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente principal — FluxoMedClienteDetail
// ─────────────────────────────────────────────────────────────
export function FluxoMedClienteDetail({ clienteId, onBack, onDeleted, onSaved }) {
  const [cliente,     setCliente]     = useState(null);
  const [implantacao, setImplantacao] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState("geral");
  const [deleting,    setDeleting]    = useState(false);

  const load = useCallback(async () => {
    const [clienteData, implData] = await Promise.all([
      db("fluxomed_clientes",    "GET", null, `?id=eq.${clienteId}`),
      db("fluxomed_implantacao", "GET", null, `?cliente_id=eq.${clienteId}`),
    ]);
    setCliente(clienteData?.[0] || null);
    setImplantacao(implData?.[0] || null);
    setLoading(false);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(patch) {
    const prevStatus = cliente?.status;
    await db("fluxomed_clientes", "PATCH",
      { ...patch, updated_at: new Date().toISOString() },
      `?id=eq.${clienteId}`
    );
    setCliente((prev) => ({ ...prev, ...patch }));
    onSaved?.();

    // Eventos de pipeline (fire-and-forget)
    if (patch.status && patch.status !== prevStatus) {
      db("fluxomed_historico", "POST", {
        cliente_id: clienteId,
        evento:     "pipeline_avancado",
        descricao:  `Pipeline: ${PIPELINE_STATUS_CFG[prevStatus]?.label || prevStatus} → ${PIPELINE_STATUS_CFG[patch.status]?.label || patch.status}`,
        dados:      { de: prevStatus, para: patch.status },
      }).catch((e) => console.error("[FluxoMed] pipeline_avancado:", e));

      if (patch.status === "producao") {
        db("fluxomed_historico", "POST", {
          cliente_id: clienteId,
          evento:     "cliente_producao",
          descricao:  "Cliente entrou em produção",
          dados:      { data: new Date().toISOString() },
        }).catch((e) => console.error("[FluxoMed] cliente_producao:", e));
      }
      if (patch.status === "pausado") {
        db("fluxomed_historico", "POST", {
          cliente_id: clienteId,
          evento:     "cliente_pausado",
          descricao:  "Cliente pausado",
        }).catch((e) => console.error("[FluxoMed] cliente_pausado:", e));
      }
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Remover "${cliente?.nome}"? Esta ação é irreversível.`)) return;
    setDeleting(true);
    await db("fluxomed_clientes", "DELETE", null, `?id=eq.${clienteId}`);
    onDeleted?.();
  }

  const TABS = [
    { id: "geral",        label: "Geral"        },
    { id: "convenios",    label: "Convênios"    },
    { id: "unidades",     label: "Unidades"     },
    { id: "mensagens",    label: "Mensagens"    },
    { id: "followup",     label: "Follow-up"    },
    { id: "homologacao",  label: "Homologação"  },
    { id: "exportar",     label: "Exportar"     },
    { id: "historico",    label: "Histórico"    },
    { id: "implantacao",  label: "Implantação"  },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 12 }}>
        <Spinner size={24} />
        <span style={{ fontSize: 13, color: "var(--muted)" }}>Carregando cliente...</span>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div style={{ padding: 28 }}>
        <Btn variant="ghost" sm onClick={onBack}>← Voltar</Btn>
        <Empty icon="◎" text="Cliente não encontrado." />
      </div>
    );
  }

  const sCfg = STATUS_CFG[cliente.status] || STATUS_CFG.rascunho;
  const aCfg = AMBIENTE_CFG[cliente.ambiente] || AMBIENTE_CFG.lab;

  const slug = (cliente.nome || "").toLowerCase().normalize("NFD").replace(/[0300-036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const nomes = (cliente.nome || "").trim().split(/\s+/);

  return (
    <div className="view" style={{ maxWidth: 960 }}>
      <Stage
        produto="crm"
        path={`fluxomed/clientes/${slug}`}
        title={nomes.length > 1 ? nomes.slice(0, -1).join(" ") : ""}
        hl={nomes[nomes.length - 1]}
        lead={
          <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Selo tone={sCfg.tone}>{sCfg.label}</Selo>
            <Selo tone={aCfg.tone} dot={false}>{aCfg.label}</Selo>
            {cliente.origem_template && <Selo tone="off" dot={false}>{cliente.origem_template}</Selo>}
            {cliente.medico_nome && <span>{cliente.medico_nome}</span>}
            {cliente.instancia_evolution && <span>· {cliente.instancia_evolution}</span>}
          </span>
        }
        actions={
          <>
            <Btn sm variant="secondary" onClick={onBack}>← Clientes</Btn>
            <Btn sm variant="ghost" onClick={handleDelete} disabled={deleting}>{deleting ? "..." : "Excluir"}</Btn>
          </>
        }
        tabs={<StageTabs label="Seções do cliente FluxoMed" items={TABS} active={tab} onChange={setTab} />}
      />

      {/* ── Conteúdo ────────────────────────────────────────── */}
      <div>
        {tab === "geral"        && <TabGeral        cliente={cliente} implantacao={implantacao} onUpdate={handleUpdate} />}
        {tab === "convenios"    && <TabConvenios    clienteId={clienteId} />}
        {tab === "unidades"     && <TabUnidades     clienteId={clienteId} />}
        {tab === "mensagens"    && <TabMensagens    clienteId={clienteId} />}
        {tab === "followup"     && <TabFollowup     cliente={cliente} onUpdate={handleUpdate} />}
        {tab === "homologacao"  && <TabHomologacao  cliente={cliente} onUpdate={handleUpdate} />}
        {tab === "exportar"     && <TabExportar     cliente={cliente} clienteId={clienteId} />}
        {tab === "historico"    && <TabHistorico    clienteId={clienteId} />}
        {tab === "implantacao"  && <TabImplantacao  cliente={cliente} clienteId={clienteId} />}
      </div>
    </div>
  );
}
